import { useEffect, useRef, useState } from 'react';
import {
	getAuth,
	verifyPasswordResetCode,
	confirmPasswordReset,
} from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ModalComponent from '../../components/modal/modal.component';
import {
	TextField,
	Box,
	Typography,
	IconButton,
	InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ResetPasswordPage = () => {
	const [searchParams] = useSearchParams();
	const [email, setEmail] = useState<string | null>(null);
	const [oobCodeValid, setOobCodeValid] = useState(false);
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState<
		'checking' | 'ready' | 'success' | 'error'
	>('checking');

	const hasRun = useRef(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const auth = getAuth();
		const oobCode = searchParams.get('oobCode');

		if (!oobCode) {
			setStatus('error');
			setError('Código inválido.');
			return;
		}

		verifyPasswordResetCode(auth, oobCode)
			.then((email) => {
				setEmail(email);
				setOobCodeValid(true);
				setStatus('ready');
			})
			.catch(() => {
				setStatus('error');
				setError('El enlace no es válido o ha expirado.');
			});
	}, [searchParams]);

	const handlePasswordReset = async () => {
		const auth = getAuth();
		const oobCode = searchParams.get('oobCode');

		if (!newPassword || !confirmPassword) {
			setError('Por favor completa ambos campos.');
			return;
		}

		const passwordTooShort =
			!/^(?=.{8,})/.test(newPassword) || !/^(?=.{8,})/.test(confirmPassword);

		if (passwordTooShort) {
			setError('La contraseña debe tener al menos 8 caracteres.');
			return;
		}

		if (newPassword !== confirmPassword) {
			setError('Las contraseñas no coinciden.');
			return;
		}

		try {
			await confirmPasswordReset(auth, oobCode!, newPassword);
			setStatus('success');
		} catch (error: any) {
			console.error('Error resetting password:', error.message);
			setError('Hubo un problema actualizando tu contraseña.');
			setStatus('error');
		}
	};

	const [showPassword, setShowPassword] = useState(false);

	const handleTogglePasswordVisibility = () => {
		setShowPassword((prev) => !prev);
	};

	const modalContent = {
		checking: {
			title: 'Verificando el enlace...',
			description: '',
		},
		ready: {
			title: 'Restablecer Contraseña',
			description: (
				<Box>
					<TextField
						fullWidth
						type={showPassword ? 'text' : 'password'}
						label="Nueva contraseña"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						margin="normal"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={handleTogglePasswordVisibility}
										edge="end"
									>
										{showPassword ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<TextField
						fullWidth
						type={showPassword ? 'text' : 'password'}
						label="Confirmar contraseña"
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						margin="normal"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={handleTogglePasswordVisibility}
										edge="end"
									>
										{showPassword ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					{error && (
						<Box sx={{ mt: 2, color: 'red' }}>
							<Typography variant="body2">{error}</Typography>
						</Box>
					)}
				</Box>
			),
			buttonText: 'Restablecer',
			onButtonClick: handlePasswordReset,
		},
		success: {
			title: '¡Contraseña actualizada!',
			description: 'Ahora puedes iniciar sesión con tu nueva contraseña.',
			buttonText: 'Ir al inicio',
			onButtonClick: () => navigate('/login'),
		},
		error: {
			title: 'Error',
			description: error || 'Hubo un problema restableciendo tu contraseña.',
			buttonText: 'Volver al inicio',
			onButtonClick: () => navigate('/login'),
		},
	};

	const content = modalContent[status];
	const { title, description } = content;
	const buttonText = 'buttonText' in content ? content.buttonText : undefined;
	const onButtonClick =
		'onButtonClick' in content ? content.onButtonClick : undefined;

	return (
		<ModalComponent
			open
			title={title}
			description={description}
			imageSrc="/cambiapp_logo.png"
			buttonText={buttonText || 'Cerrar'}
			onClose={() => navigate('/login')}
			onButtonClick={onButtonClick}
			showCloseButton={status !== 'checking'}
		/>
	);
};

export default ResetPasswordPage;
